/**
 * Alignment History Chart Component
 * Shows user's alignment trends over the past week with insights
 */

import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { TrendingUp, TrendingDown, Minus, BarChart3, Sparkles, RefreshCw } from "lucide-react";
import axios from "axios";

const API_URL = process.env.REACT_APP_BACKEND_URL;

// State color mapping
const getStateColor = (value) => {
  if (value <= 3) return "#6B7280"; // Grey
  if (value <= 5) return "#64748B"; // Slate
  if (value <= 6) return "#4A6759"; // Green-grey
  if (value <= 7) return "#4A6741"; // Reclaim green
  if (value <= 8) return "#22C55E"; // Green
  return "#10B981"; // Emerald
};

const STATE_MAPPING = {
  1: "Disconnected",
  2: "Closed",
  3: "Overwhelmed",
  4: "Unsettled",
  5: "Neutral",
  6: "Settling",
  7: "Open",
  8: "Aligned",
  9: "Flow",
  10: "Connected"
};

export const AlignmentHistoryChart = () => {
  const [loading, setLoading] = useState(true);
  const [summary, setSummary] = useState(null);
  const [history, setHistory] = useState([]);
  
  const getToken = () => localStorage.getItem("reclaim_token");
  
  const loadData = async () => {
    setLoading(true);
    try {
      const [summaryRes, historyRes] = await Promise.all([
        axios.get(`${API_URL}/api/alignment/weekly-summary`, {
          headers: { Authorization: `Bearer ${getToken()}` }
        }),
        axios.get(`${API_URL}/api/alignment/history?days=7`, {
          headers: { Authorization: `Bearer ${getToken()}` }
        })
      ]);
      
      setSummary(summaryRes.data);
      setHistory(historyRes.data.alignments || []);
    } catch (err) {
      console.error("Error loading alignment data:", err);
    } finally {
      setLoading(false);
    }
  };
  
  useEffect(() => {
    loadData();
  }, []);
  
  if (loading) {
    return (
      <div className="bg-gradient-to-br from-[#F9FAFB] to-[#F3F4F6] border border-[#E6E2DD] rounded-2xl p-5">
        <div className="flex items-center justify-center h-32">
          <div className="w-6 h-6 border-2 border-[#4A6741]/20 border-t-[#4A6741] rounded-full animate-spin" />
        </div>
      </div>
    );
  }
  
  if (!summary?.has_data) {
    return (
      <div className="bg-gradient-to-br from-[#F9FAFB] to-[#F3F4F6] border border-[#E6E2DD] rounded-2xl p-5">
        <div className="flex items-center gap-3 mb-3">
          <div className="w-10 h-10 rounded-xl bg-[#4A6741]/10 flex items-center justify-center">
            <BarChart3 className="text-[#4A6741]" size={20} />
          </div>
          <div>
            <h3 className="font-heading text-lg text-[#292524]">Alignment Trends</h3>
            <p className="text-sm text-[#78716C]">Your weekly patterns</p>
          </div>
        </div>
        <p className="text-center text-[#78716C] py-6 text-sm">
          Complete your first Alignment Check to see your trends!
        </p>
      </div>
    );
  }
  
  const { stats, desired_states, insights } = summary;
  const trend = stats.trend;
  const TrendIcon = trend === "improving" ? TrendingUp : trend === "declining" ? TrendingDown : Minus;
  const trendColor = trend === "improving" ? "text-emerald-600" : trend === "declining" ? "text-red-500" : "text-slate-500";
  
  // Get last 7 days for the chart
  const last7Days = [];
  for (let i = 6; i >= 0; i--) {
    const date = new Date();
    date.setDate(date.getDate() - i);
    const dateStr = date.toISOString().split('T')[0];
    const dayName = date.toLocaleDateString('en', { weekday: 'short' });
    const alignment = history.find(a => a.date === dateStr);
    last7Days.push({
      date: dateStr,
      day: dayName,
      value: alignment?.slider_value || null,
      state: alignment?.state_word || null
    });
  }
  
  // Find most sought state title
  const mostSoughtId = desired_states?.most_sought;
  const mostSoughtTitle = mostSoughtId 
    ? mostSoughtId.charAt(0).toUpperCase() + mostSoughtId.slice(1)
    : null;
  
  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-gradient-to-br from-[#F9FAFB] to-[#F3F4F6] border border-[#E6E2DD] rounded-2xl p-5 overflow-hidden"
    >
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-[#4A6741]/10 flex items-center justify-center">
            <BarChart3 className="text-[#4A6741]" size={20} />
          </div>
          <div>
            <h3 className="font-heading text-lg text-[#292524]">Alignment Trends</h3>
            <p className="text-sm text-[#78716C]">Past 7 days</p>
          </div>
        </div>
        <button
          onClick={loadData}
          className="p-2 rounded-lg hover:bg-white/50 transition-colors"
          aria-label="Refresh"
        >
          <RefreshCw size={16} className="text-[#78716C]" />
        </button>
      </div>
      
      {/* Stats Row */}
      <div className="grid grid-cols-3 gap-3 mb-5">
        <div className="bg-white/60 rounded-xl p-3 text-center">
          <p className="text-2xl font-bold text-[#4A6741]">{stats.average_value}</p>
          <p className="text-xs text-[#78716C] mt-0.5">Avg State</p>
          <p className="text-xs font-medium text-[#4A6741] mt-0.5">{stats.average_state}</p>
        </div>
        <div className="bg-white/60 rounded-xl p-3 text-center">
          <p className="text-2xl font-bold text-[#292524]">{stats.total_checks}</p>
          <p className="text-xs text-[#78716C] mt-0.5">Check-ins</p>
        </div>
        <div className="bg-white/60 rounded-xl p-3 text-center">
          <div className={`flex items-center justify-center gap-1 ${trendColor}`}>
            <TrendIcon size={18} />
            <span className="text-sm font-medium capitalize">{trend}</span>
          </div>
          <p className="text-xs text-[#78716C] mt-0.5">Trend</p>
          {stats.trend_delta !== 0 && (
            <p className={`text-xs ${trendColor} mt-0.5`}>
              {stats.trend_delta > 0 ? "+" : ""}{stats.trend_delta}
            </p>
          )}
        </div>
      </div>
      
      {/* Bar Chart */}
      <div className="bg-white/40 rounded-xl p-4 mb-4">
        <div className="flex items-end justify-between gap-2 h-24">
          {last7Days.map((day, idx) => {
            const height = day.value ? (day.value / 10) * 100 : 0;
            const color = day.value ? getStateColor(day.value) : "#E5E7EB";
            const isToday = idx === 6;
            
            return (
              <div key={day.date} className="flex-1 flex flex-col items-center">
                <div className="relative w-full flex items-end justify-center h-20">
                  <motion.div
                    initial={{ height: 0 }}
                    animate={{ height: `${height}%` }}
                    transition={{ duration: 0.5, delay: idx * 0.05 }}
                    className="w-full max-w-[24px] rounded-t-md relative group"
                    style={{ backgroundColor: color }}
                  >
                    {/* Tooltip */}
                    {day.value && (
                      <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-1 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-10">
                        <div className="bg-[#292524] text-white text-xs px-2 py-1 rounded whitespace-nowrap">
                          {day.state} ({day.value})
                        </div>
                      </div>
                    )}
                    {/* Value label */}
                    {day.value && (
                      <span className="absolute -top-5 left-1/2 -translate-x-1/2 text-xs font-medium" style={{ color }}>
                        {day.value}
                      </span>
                    )}
                  </motion.div>
                  {!day.value && (
                    <div className="w-full max-w-[24px] h-1 bg-[#E5E7EB] rounded" />
                  )}
                </div>
                <p className={`text-xs mt-2 ${isToday ? "font-semibold text-[#4A6741]" : "text-[#78716C]"}`}>
                  {day.day}
                </p>
              </div>
            );
          })}
        </div>
      </div>
      
      {/* Most Sought State */}
      {mostSoughtTitle && desired_states.most_sought_count > 0 && (
        <div className="flex items-center gap-2 text-sm text-[#78716C] mb-3">
          <Sparkles size={14} className="text-[#D4A373]" />
          <span>
            You've been seeking <span className="font-medium text-[#292524]">{mostSoughtTitle}</span> the most ({desired_states.most_sought_count}×)
          </span>
        </div>
      )}
      
      {/* Insights */}
      {insights && insights.length > 0 && (
        <div className="border-t border-[#E6E2DD] pt-3 mt-1">
          <AnimatePresence mode="wait">
            <motion.p
              key={insights[0]}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="text-sm text-[#78716C] italic"
            >
              "{insights[0]}"
            </motion.p>
          </AnimatePresence>
        </div>
      )}
    </motion.div>
  );
};

export default AlignmentHistoryChart;
